/**
 * Schadens-Kalkulation (Reparaturkosten)
 * ---------------------------------------
 * Table editor grouped by category (Arbeitslohn · Ersatzteile · Lackierung ·
 * Nebenkosten).  Totals are re-computed live in the UI and once more on the
 * server during save so the DB / PDF are authoritative.
 *
 * Props:
 *  - value:   CalculationData object  (items, hourly rates, tax_rate, ...)
 *  - onChange(next):  setter
 *  - onSave():        async; caller persists via PATCH /cases/{id}
 *  - saving:          boolean
 *  - readOnly:        boolean
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Loader2, Calculator, Wrench, Package, Paintbrush, Layers } from "lucide-react";

const CATEGORIES = [
  { key: "labor",     label: "Arbeitslohn",   icon: Wrench,     unit: "Std",      accent: "cyan"    },
  { key: "parts",     label: "Ersatzteile",   icon: Package,    unit: "Stk",      accent: "emerald" },
  { key: "paint",     label: "Lackierung",    icon: Paintbrush, unit: "pauschal", accent: "violet"  },
  { key: "surcharge", label: "Nebenkosten",   icon: Layers,     unit: "pauschal", accent: "amber"   },
];

const ACCENT_CLASSES = {
  cyan:    { bg: "bg-cyan-50",    border: "border-cyan-200",    text: "text-cyan-700",    iconBg: "bg-cyan-100"    },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", iconBg: "bg-emerald-100" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-700",  iconBg: "bg-violet-100"  },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   iconBg: "bg-amber-100"   },
};

const fmtEur = (v) =>
  (Number(v || 0)).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

const newItem = (category, unit) => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  category,
  description: "",
  quantity: 1,
  unit_label: unit,
  unit_price: 0,
  surcharge_pct: 0,
  total_net: 0,
});

const computeRow = (it) => {
  const q = Number(it.quantity || 0);
  const p = Number(it.unit_price || 0);
  const sur = Number(it.surcharge_pct || 0);
  return Math.round(q * p * (1 + sur / 100) * 100) / 100;
};

export default function CalculationStep({ value, onChange, onSave, saving = false, readOnly = false }) {
  const calc = value || {};
  const items = calc.items || [];
  const taxRate = Number(calc.tax_rate ?? 19);

  // Pre-computed totals for live display
  const byCat = { labor: 0, parts: 0, paint: 0, surcharge: 0 };
  items.forEach((it) => {
    const t = computeRow(it);
    const k = it.category in byCat ? it.category : "surcharge";
    byCat[k] += t;
  });
  const subtotal = Math.round((byCat.labor + byCat.parts + byCat.paint + byCat.surcharge) * 100) / 100;
  const taxAmount = Math.round(subtotal * taxRate) / 100;
  const totalGross = Math.round((subtotal + taxAmount) * 100) / 100;

  const setItems = (next) => onChange({ ...calc, items: next });

  const addItem = (cat) => {
    const catDef = CATEGORIES.find((c) => c.key === cat);
    setItems([...items, newItem(cat, catDef?.unit || "Stk")]);
  };

  const updateItem = (id, patch) => {
    const next = items.map((it) => {
      if (it.id !== id) return it;
      const merged = { ...it, ...patch };
      merged.total_net = computeRow(merged);
      return merged;
    });
    setItems(next);
  };

  const removeItem = (id) => setItems(items.filter((it) => it.id !== id));

  return (
    <div className="w-full space-y-5" data-testid="step-calculation">
      <div className="flex items-start gap-4 pb-4 border-b border-zinc-200">
        <div className="h-11 w-11 rounded-xl brand-gradient-soft flex items-center justify-center shrink-0">
          <Calculator className="h-5 w-5 text-cyan-700" />
        </div>
        <div className="flex-1">
          <div className="font-display text-xl font-bold tracking-tight">Schadens-Kalkulation</div>
          <div className="text-sm text-zinc-500">Reparaturkosten — Arbeitslohn · Ersatzteile · Lackierung · Nebenkosten</div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">MwSt-Satz</Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            max="30"
            className="w-20 h-9 text-right"
            value={taxRate}
            onChange={(e) => onChange({ ...calc, tax_rate: Number(e.target.value) || 0 })}
            disabled={readOnly}
            data-testid="calc-tax-rate"
          />
          <span className="text-xs text-zinc-500">%</span>
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const rows = items.filter((it) => (it.category || "surcharge") === cat.key);
        const Icon = cat.icon;
        const acc = ACCENT_CLASSES[cat.accent];
        const subtotalCat = byCat[cat.key];
        return (
          <div
            key={cat.key}
            className={`rounded-2xl border ${acc.border} ${acc.bg} overflow-hidden`}
            data-testid={`calc-category-${cat.key}`}
          >
            <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-200/50">
              <div className={`h-8 w-8 rounded-lg ${acc.iconBg} flex items-center justify-center`}>
                <Icon className={`h-4 w-4 ${acc.text}`} strokeWidth={1.9} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-display font-semibold text-sm ${acc.text}`}>{cat.label}</div>
                <div className="text-[11px] text-zinc-500">{rows.length} Position{rows.length === 1 ? "" : "en"}</div>
              </div>
              <div className={`font-mono font-semibold text-sm ${acc.text}`} data-testid={`calc-subtotal-${cat.key}`}>
                {fmtEur(subtotalCat)}
              </div>
            </div>

            {rows.length > 0 && (
              <div className="bg-white divide-y divide-zinc-100">
                {/* Header */}
                <div className={`hidden md:grid ${cat.key === "parts" ? "grid-cols-[1fr_80px_80px_110px_90px_110px_40px]" : "grid-cols-[1fr_80px_80px_110px_110px_40px]"} gap-2 px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-zinc-500 bg-zinc-50/60`}>
                  <div>Bezeichnung</div>
                  <div className="text-right">Menge</div>
                  <div>Einheit</div>
                  <div className="text-right">Einzelpreis</div>
                  {cat.key === "parts" && <div className="text-right">Aufschlag %</div>}
                  <div className="text-right">Summe</div>
                  <div></div>
                </div>
                {rows.map((it) => (
                  <div
                    key={it.id}
                    className={`${cat.key === "parts" ? "md:grid md:grid-cols-[1fr_80px_80px_110px_90px_110px_40px]" : "md:grid md:grid-cols-[1fr_80px_80px_110px_110px_40px]"} gap-2 px-4 py-3 flex flex-col md:items-center`}
                    data-testid={`calc-row-${it.id}`}
                  >
                    <Input
                      value={it.description}
                      onChange={(e) => updateItem(it.id, { description: e.target.value })}
                      placeholder="z. B. Stoßstange vorne erneuern"
                      disabled={readOnly}
                      data-testid={`calc-desc-${it.id}`}
                    />
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      className="md:text-right"
                      value={it.quantity}
                      onChange={(e) => updateItem(it.id, { quantity: Number(e.target.value) || 0 })}
                      disabled={readOnly}
                      data-testid={`calc-qty-${it.id}`}
                    />
                    <Input
                      value={it.unit_label || ""}
                      onChange={(e) => updateItem(it.id, { unit_label: e.target.value })}
                      placeholder="Std"
                      disabled={readOnly}
                      data-testid={`calc-unit-${it.id}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="md:text-right"
                      value={it.unit_price}
                      onChange={(e) => updateItem(it.id, { unit_price: Number(e.target.value) || 0 })}
                      disabled={readOnly}
                      data-testid={`calc-price-${it.id}`}
                    />
                    {cat.key === "parts" && (
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        className="md:text-right"
                        value={it.surcharge_pct}
                        onChange={(e) => updateItem(it.id, { surcharge_pct: Number(e.target.value) || 0 })}
                        placeholder="0"
                        disabled={readOnly}
                        data-testid={`calc-surcharge-${it.id}`}
                      />
                    )}
                    <div className="md:text-right font-mono font-semibold text-sm text-zinc-900" data-testid={`calc-total-${it.id}`}>
                      {fmtEur(computeRow(it))}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(it.id)}
                      disabled={readOnly}
                      className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 justify-self-end"
                      title="Position löschen"
                      data-testid={`calc-del-${it.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="px-4 py-2 border-t border-zinc-200/50 bg-white/70">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => addItem(cat.key)}
                disabled={readOnly}
                className={`${acc.text} hover:bg-zinc-100 h-8`}
                data-testid={`calc-add-${cat.key}`}
              >
                <Plus className="h-4 w-4 mr-1.5" /> Position hinzufügen
              </Button>
            </div>
          </div>
        );
      })}

      {/* Totals */}
      <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50/40 p-5" data-testid="calc-totals">
        <div className="space-y-2 text-sm">
          <Row label="Arbeitslohn"  value={byCat.labor}     muted />
          <Row label="Ersatzteile"  value={byCat.parts}     muted />
          <Row label="Lackierung"   value={byCat.paint}     muted />
          <Row label="Nebenkosten"  value={byCat.surcharge} muted />
          <div className="h-px bg-emerald-200 my-1" />
          <Row label="Summe netto" value={subtotal} strong testid="calc-subtotal" />
          <Row label={`zzgl. MwSt. ${taxRate}%`} value={taxAmount} muted />
          <div className="h-px bg-emerald-300 my-1" />
          <div className="flex items-center justify-between pt-1">
            <div className="font-display font-bold text-lg text-emerald-900">Gesamt brutto</div>
            <div className="font-display font-bold text-xl text-emerald-800 font-mono" data-testid="calc-total-gross">
              {fmtEur(totalGross)}
            </div>
          </div>
        </div>
      </div>

      {/* Note */}
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Anmerkung zur Kalkulation</Label>
        <Textarea
          className="mt-1.5"
          rows={3}
          value={calc.calculation_note || ""}
          onChange={(e) => onChange({ ...calc, calculation_note: e.target.value })}
          disabled={readOnly}
          placeholder="z. B. Stundensätze entsprechen Markenvorgabe BMW AG …"
          data-testid="calc-note"
        />
      </div>

      {/* Save */}
      {onSave && !readOnly && (
        <div className="flex gap-3 pt-2">
          <Button onClick={onSave} disabled={saving} className="brand-gradient-bg text-white" data-testid="calc-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Kalkulation speichern
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, strong, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={muted ? "text-zinc-600" : strong ? "font-semibold text-zinc-900" : ""}>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>
        {fmtEur(value)}
      </span>
    </div>
  );
}
